/**
 * Konfiguracja TanStack Query + klucze zapytan + hooki na wspolnych danych.
 *
 * Jedno miejsce na klucze, bo uniewaznianie po mutacji musi byc przewidywalne:
 * `queryKeys.products.list(...)` jest prefiksowany przez `queryKeys.products.all`,
 * wiec `invalidateQueries({ queryKey: queryKeys.products.all })` czysci wszystkie
 * strony listy bez zgadywania parametrow.
 */

import { QueryClient, useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import type { MeResponse, ModelsResponse } from '@shared/api-types';
import type { ModelRegistrySnapshot } from '@shared/models/capabilities';
import { MODEL_REGISTRY_SNAPSHOT_VERSION } from '@shared/models/capabilities';
import type { ClientId, GenerationId, ProductId, SceneId } from '@shared/ids';
import { ApiClientError, api } from './api';

/** Retry ma sens tylko dla bledow przejsciowych — 4xx nie naprawi sie samo. */
function retryPolicy(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiClientError) {
    if (error.status === 401 || error.status === 403 || error.status === 404) return false;
    if (error.status === 501) return false;
    if (error.status < 500 && error.status !== 429) return false;
  }
  return failureCount < 2;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: retryPolicy,
        refetchOnWindowFocus: false,
        staleTime: 15_000,
      },
      mutations: { retry: 0 },
    },
  });
}

export const queryKeys = {
  health: ['health'] as const,
  me: ['me'] as const,
  models: ['models'] as const,
  billing: ['billing', 'summary'] as const,
  costs: (period?: string) => ['costs', 'summary', period ?? 'current'] as const,
  members: (accountId: string) => ['members', accountId] as const,
  clients: {
    all: ['clients'] as const,
    list: (includeArchived: boolean) => ['clients', 'list', includeArchived] as const,
    detail: (id: ClientId) => ['clients', 'detail', id] as const,
  },
  categories: {
    all: ['categories'] as const,
    tree: (clientId: ClientId) => ['categories', 'tree', clientId] as const,
  },
  products: {
    all: ['products'] as const,
    list: (params: Readonly<Record<string, unknown>>) => ['products', 'list', params] as const,
    detail: (id: ProductId) => ['products', 'detail', id] as const,
  },
  scenes: {
    all: ['scenes'] as const,
    list: (params: Readonly<Record<string, unknown>>) => ['scenes', 'list', params] as const,
    detail: (id: SceneId) => ['scenes', 'detail', id] as const,
  },
  generations: {
    all: ['generations'] as const,
    detail: (id: GenerationId) => ['generations', 'detail', id] as const,
  },
  gallery: (params: Readonly<Record<string, unknown>>) => ['gallery', params] as const,
} as const;

/** `GET /api/me` — tozsamosc, rola, plan i branding w jednym zapytaniu. */
export function useMe(): UseQueryResult<MeResponse, ApiClientError> {
  return useQuery<MeResponse, ApiClientError>({
    queryKey: queryKeys.me,
    queryFn: () => api.me(),
    // Brak sesji to normalny stan aplikacji, nie awaria — 401 nie ma byc retry'owane.
    retry: false,
    staleTime: 60_000,
  });
}

/**
 * `GET /api/models` — JEDYNE zrodlo listy modeli i cennika w UI.
 * Zadnych zahardkodowanych id modeli ani cen w komponentach.
 */
export function useModels(): UseQueryResult<ModelsResponse, ApiClientError> {
  return useQuery<ModelsResponse, ApiClientError>({
    queryKey: queryKeys.models,
    queryFn: () => api.models(),
    staleTime: 5 * 60_000,
  });
}

/** Rejestr w postaci, ktorej oczekuja `pickModel` i `estimateCost` z `shared`. */
export function registrySnapshotOf(models: ModelsResponse): ModelRegistrySnapshot {
  return {
    version: MODEL_REGISTRY_SNAPSHOT_VERSION,
    syncedAt: models.syncedAt,
    models: models.models,
  };
}
